import * as d3 from 'd3';
import { indigo, grey, red } from '@mui/material/colors';

import { ESGImpactHexBorderMap } from 'shared/constants/ESGImpactHexCodesMap';

const defaultColors = {
  background: grey[500],
  indicator: red[700],
  average: grey[500],
  secondary: indigo[400],
};

const defaultLabels = {
  average: 'Avg.',
  worst: 'Worst',
  best: 'Best',
};

const pieGenerator = d3
  .pie()
  .value(d => d.value)
  .startAngle(-90 * (Math.PI / 180))
  .endAngle(90 * (Math.PI / 180))
  .sort(null);

const getEndAnglePosition = ({ innerRadius, outerRadius, startAngle, endAngle }) => {
  const r = (+innerRadius + +outerRadius) / 2;
  const a = +startAngle + +endAngle - Math.PI;
  return [Math.cos(a) * r, Math.sin(a) * r];
};

class GougeChartSvg {
  constructor({
    current,
    small,
    width,
    height,
    value,
    worst,
    average,
    best,
    color = ESGImpactHexBorderMap.medium,
    labels = defaultLabels,
    factor = 100,
  }) {
    this.current = current;
    this.small = small;
    this.width = width;
    this.height = height;
    this.colors = color;
    this.radius = Math.min(width, height) / 2 - this.margin;
    this.labels = labels;
    this.value = value;
    this.worst = worst;
    this.average = average;
    this.best = best;
    this.factor = factor;
  }

  get margin() {
    return this.width * 0.05;
  }

  set value(val) {
    this._value = this.getTrasnformedValues(val);
  }

  get value() {
    return this._value;
  }

  set color(value) {
    this.colors = {
      ...defaultColors,
      primary: value,
    };
  }

  set worst(val) {
    this._worst = this.getTrasnformedValues(val);
  }

  get worst() {
    return this._worst;
  }

  set average(val) {
    this._average = this.getTrasnformedValues(val);
  }

  get average() {
    return this._average;
  }

  set best(val) {
    this._best = this.getTrasnformedValues(val);
  }

  get best() {
    return this._best;
  }

  init() {
    this.svg = d3.select(this.current).append('svg');

    this.container = this.svg
      .attr('width', this.width)
      .attr('height', this.height)
      .style('transform', `${this.small ? 'scale(0.6)' : 'none'}`)
      .style('overflow', `${this.small ? 'inherit' : 'hidden'}`)
      .append('g')
      .attr('transform', `translate(${this.width / 2},${this.height / 2 + this.margin * 4})`);

    this.drawBackgroundChart();
    this.drawWorstChart();
    this.drawBestChart();
    this.drawPrimaryChart();
    this.drawAverage();
  }

  getTrasnformedValues = value => [value, this.factor - value];

  drawBackgroundChart() {
    const data = { a: 1 };
    const dataReady = pieGenerator(d3.entries(data));

    this.container
      .selectAll('backgroundChartElements')
      .data(dataReady)
      .enter()
      .append('path')
      .attr(
        'd',
        d3
          .arc()
          .innerRadius(this.radius + 3)
          .outerRadius(this.radius + 8),
      )
      .attr('fill', this.colors.background)
      .exit();
  }

  drawPrimaryChart() {
    const data = { a: this.value[0], b: this.value[1] };
    const color = d3
      .scaleOrdinal()
      .domain(data)
      .range([this.colors.primary, 'transparent']);

    const dataReady = pieGenerator(d3.entries(data));

    this.container
      .selectAll('mainChartElements')
      .data(dataReady)
      .enter()
      .append('path')
      .attr(
        'd',
        d3
          .arc()
          .innerRadius(this.radius + 10)
          .outerRadius(this.radius),
      )
      .attr('fill', d => color(d.data.key));

    this.drawPolylines(data, this.colors.indicator);
  }

  drawWorstChart() {
    const data = { a: this.worst[0], [this.labels.worst]: this.worst[1] };
    const color = d3
      .scaleOrdinal()
      .domain(data)
      .range(['transparent', this.colors.secondary, 'transparent']);
    const dataReady = pieGenerator(d3.entries(data));

    this.container
      .selectAll('secondaryChartElements')
      .data(dataReady)
      .enter()
      .append('path')
      .attr(
        'd',
        d3
          .arc()
          .innerRadius(this.radius + 20)
          .outerRadius(this.radius + 30),
      )
      .attr('fill', d => color(d.data.key))
      .exit();

    this.drawPolylinesWithLabelsWithLabels(data, this.colors.secondary, 20, 0);
  }

  drawBestChart() {
    const data = { a: this.best[0], [this.labels.best]: this.best[1] };
    const color = d3
      .scaleOrdinal()
      .domain(data)
      .range(['transparent', '#fff', 'transparent']);
    const dataReady = pieGenerator(d3.entries(data));

    this.container
      .selectAll('secondaryChartElements')
      .data(dataReady)
      .enter()
      .append('path')
      .attr(
        'd',
        d3
          .arc()
          .innerRadius(this.radius + 18)
          .outerRadius(this.radius + 32),
      )
      .attr('fill', d => color(d.data.key))
      .exit();

    this.drawPolylinesWithLabelsWithLabels(data, this.colors.secondary, 20, 65);
  }

  drawPolylinesWithLabelsWithLabels(data, color, distance = 0, lengthFactor = 0) {
    this.drawPolylines(data, color, distance, lengthFactor);
    this.drawLabels(data, lengthFactor);
  }

  drawPolylines(data, color, distance = 0, lengthFactor = 0) {
    const dataReady = pieGenerator(d3.entries(data));
    const getColor = d3
      .scaleOrdinal()
      .domain(data)
      .range(['transparent', color]);

    this.container
      .selectAll('polylines')
      .data(dataReady)
      .enter()
      .append('polyline')
      .attr('stroke', d => getColor(d.data.key))
      .style('fill', 'none')
      .attr('stroke-width', 1)
      .attr('points', d => {
        const posA = getEndAnglePosition({
          innerRadius: this.radius + distance,
          outerRadius: this.radius + distance,
          startAngle: d.startAngle,
          endAngle: d.endAngle,
        });

        const posB = getEndAnglePosition({
          innerRadius: this.radius,
          outerRadius: this.radius * 2 + lengthFactor,
          startAngle: d.startAngle,
          endAngle: d.endAngle,
        });

        return [posA, posB];
      });
  }

  drawLabels(data, lengthFactor) {
    const dataReady = pieGenerator(d3.entries(data));

    this.container
      .selectAll('allLabels')
      .data(dataReady)
      .enter()
      .append('text')
      .text(d => (d.data.key.length > 1 ? d.data.key : null))
      .attr('transform', d => {
        const pos = getEndAnglePosition({
          innerRadius: this.radius,
          outerRadius: this.radius * 2 + lengthFactor,
          startAngle: d.startAngle,
          endAngle: d.endAngle,
        });
        const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        pos[0] = midangle < 0.8 ? pos[0] - 25 : pos[0] - 25;
        pos[1] -= 5;

        return `translate(${pos})`;
      })
      .exit();
  }

  drawAverage() {
    this.drawPolylinesWithLabelsWithLabels(
      { a: this.average[0], [this.labels.average]: this.average[1] },
      this.colors.average,
      20,
      30,
    );
  }

  destroy() {
    this.svg.remove();
  }
}

export default GougeChartSvg;
